<template>
  <div>
    <a-table
      class="hidden md:block"
      :columns="columnsInMainRow"
      :data-source="items"
      bordered
      :size="tableSize"
      :defaultExpandAllRows="defaultExpandAllRows"
      :expanded-row-keys.sync="expandedRowKeys"
    >
      <template v-if="tableTitle !== null" slot="title">
        <div class="bg-white flex items-center">
          <a-icon @click="toggleExpandedRowKeys" :type="expandedRowKeys.length ? 'minus-square' : 'plus-square'" class="mr-2"/>
          <div class="h4 text-base font-semibold leading-6 text-gray-900">
            <a-badge :count="items.length" show-zero :number-style="{backgroundColor: items.length ? 'green' : 'red'}"></a-badge>
            {{tableTitle}}
          </div>
        </div>
      </template>

      <template v-if="tableFooter" slot="footer">
        <a-button class="editable-add-btn mb-1" type="primary" @click="$emit('onAddActivity')" size="default" :disabled="addButtonDisabled">
          {{$t('add')}}
        </a-button>
      </template>

      <div slot="expandedRowRender" slot-scope="record">
        <div class="grid grid-cols-12 gap-x-6 gap-y-4 sm:grid-cols-12" :class="`md:grid-cols-${columnsInExpandableRow.length}`">
          <div v-for="col in columnsInExpandableRow" :key="col" class="col-span-12 md:col-span-1" @keydown="handleKeyDown">
            <label :for="col" class="block text-sm font-medium leading-6 text-gray-900">{{columnTitle(col)}}</label>
            <div v-if="columnFieldType(col) === 'singleselect'">
              <a-select
                style="width: 100%"
                allowClear
                :default-value="getRecordLabelFromSelectorOptionsIfExists(col, record)"
                :placeholder="$t('multiselect_placeholder')"
                :autoFocus="columnPosition(col) === 0"
                :showSearch="true"
                :disabled="!record.editable"
                :notFoundContent="$t('not_found_content')"
                optionFilterProp="label"
                @change="items => handleChange(items, record.key, col, true)"
              >
                <a-select-option v-for="item in getSelectorOptions(col)" :key="item.key" :label="item.label">
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </div>
            <div v-else-if="columnFieldType(col) === 'multiselect'">
              <a-select
                :default-value="getSavedValue(record.key, col, true)"
                mode="multiple"
                style="width: 100%"
                :placeholder="$t('multiselect_placeholder')"
                :disabled="!record.editable"
                :autoFocus="columnPosition(col) === 0"
                :notFoundContent="$t('not_found_content')"
                optionFilterProp="label"
                @change="items => handleChange(items, record.key, col, true)"
              >
                <a-select-option v-for="item in getSelectorOptions(col)" :key="item.key" :label="item.label">
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </div>
            <div v-else class="mt-2">
              <a-textarea
                allow-clear
                :autoSize="true"
                :disabled="!record.editable"
                :value="getSavedValue(record.key, col)"
                @change="e => handleChange(e.target.value, record.key, col)"
              />
            </div>
          </div>
        </div>
      </div>

      <template
        v-for="col in columnsInMainRowButActions"
        :slot="col"
        slot-scope="value, record, index"
      >
        <div :key="col" @keydown="handleKeyDown">
          <template v-if="record.editable">
            <div v-if="columnFieldType(col) === 'timepicker'">
              <div class="pt-1 pb-1">{{columnTitle(col)}}</div>
              <a-time-picker
                :format="timePickerFormat"
                :size="timePickerSize"
                :placeholder="timePickerFormat"
                :valueFormat="timePickerFormat"
                :defaultValue="getSavedValue(record.key, col)"
                :minuteStep="timepickerMinuteStep"
                @change="time => handleChange(time, record.key, col)"
              />
            </div>
            <div v-else-if="columnFieldType(col) === 'timepicker-mask'">
              <div class="pt-1 pb-1">{{columnTitle(col)}}</div>
              <LmTimePickerMask
                :value="getSavedValue(record.key, col)"
                @change="time => handleChange(time, record.key, col)"
              />
            </div>
            <div v-else-if="columnFieldType(col) === 'datepicker'">
              <div class="pt-1 pb-1">{{columnTitle(col)}}</div>
              <a-date-picker
                :placeholder="datePickerFormat"
                :format="datePickerFormat"
                :size="datePickerSize"
                :defaultValue="getSavedValue(record.key, col)"
                :valueFormat="datePickerFormat"
                :disabled-date="datePickerDisabledDate"
                :minuteStep="timepickerMinuteStep"
                @change="date => handleChange(date, record.key, col)"
              />
            </div>
            <div v-else-if="columnFieldType(col) === 'multiselect'">
              <div class="pt-1 pb-1">{{columnTitle(col)}}</div>
              <a-select
                :default-value="getSavedValue(record.key, col, true)"
                mode="multiple"
                style="width: 100%"
                :placeholder="$t('multiselect_placeholder')"
                :autoFocus="columnPosition(col) === 0"
                :notFoundContent="$t('not_found_content')"
                optionFilterProp="label"
                @change="items => handleChange(items, record.key, col, true)"
              >
                <a-select-option v-for="item in getSelectorOptions(col)" :key="item.key" :label="item.label">
                  {{ item.label }}
                  <a-switch
                    size="small"
                    :checked="getPrimaryPassenger(record, item)"
                    @change="setPrimaryPassenger(record, item)"/>
                </a-select-option>
              </a-select>
            </div>
            <div v-else-if="columnFieldType(col) === 'singleselect'">
              <div class="pt-1 pb-1">{{columnTitle(col)}}</div>
              <a-select
                style="width: 100%"
                allowClear
                :default-value="getRecordLabelFromSelectorOptionsIfExists(col, record)"
                :placeholder="$t('multiselect_placeholder')"
                :autoFocus="columnPosition(col) === 0"
                :showSearch="true"
                :notFoundContent="$t('not_found_content')"
                optionFilterProp="label"
                @change="items => handleChange(items, record.key, col, true)"
              >
                <a-select-option v-for="item in getSelectorOptions(col)" :key="item.key" :label="item.label">
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </div>
            <div v-else>
              <div class="pt-1 pb-1">{{columnTitle(col)}}</div>
              <a-input
                :label="columnTitle(col)"
                :placeholder="columnTitle(col)"
                style="margin: -5px 0"
                :value="value"
                @change="e => handleChange(e.target.value, record.key, col)"
              />
            </div>
          </template>
          <template v-else>
            <div v-if="columnFieldType(col) === 'multiselect'" >
              <router-link v-for="tag in getRecordsLabelsFromSelectorOptions(col, value)" :key="tag.key" :to="`${columnTargetLink(col, tag.key, record)}`">
              <a-tag
                class="m-1 cursor-pointer"
                :key="tag.key"
                :color="tag.key === record.primaryPassenger? 'green' : columnFieldTagColor(col)"
              >
                <a-badge>
                  <div class="mt-1"> {{tag.label}} </div> <span>{{tag.key === record.primaryPassenger && (value?.length - 1 > 0)? `+${value?.length - 1}` : ''}}</span>
                  <span v-if="tag?.icon" slot="count"> {{tag?.icon}} </span>
                </a-badge>
              </a-tag>
              </router-link>
              <template v-if="getRecordsLabelsFromSelectorOptions(col, value) === null">
                -
              </template>
            </div>
            <div v-else-if="columnFieldType(col) === 'singleselect'" >
              <router-link v-for="tag in getRecordsLabelsFromSelectorOptions(col, value)" :key="tag.key" :to="`${columnTargetLink(col, tag.key, record)}`">
              <a-tag
                class="m-1 cursor-pointer"
                :key="tag.key"
                :color="columnFieldTagColor(col)"
              >
                <a-badge>
                  <div class="mt-1"> {{tag.label}} </div>
                  <span v-if="tag?.icon" slot="count"> {{tag?.icon}} </span>
                </a-badge>
              </a-tag>
              </router-link>
              <template v-if="getRecordsLabelsFromSelectorOptions(col, value) === null">
                -
              </template>
            </div>
            <div v-else>
              <template v-if="value">
                <template v-if="dailyTargetLink(col, value)">
                  <router-link :to="dailyTargetLink(col, value)">
                    {{value}}
                  </router-link>
                </template>
                <template v-else>
                  {{ value }}
                </template>
              </template>
              <template v-else>
                -
              </template>
            </div>
          </template>
        </div>
      </template>
      <template slot="actionSlot" slot-scope="text, record, index">
        <div class="editable-row-operations">
          <span v-if="record.editable">
            <a @click="() => save(record)">
              <a-tooltip :title="$t('save')" placement="bottom">
                <a-icon type="save" theme="twoTone"/>
              </a-tooltip>
            </a>
            <a-popconfirm :title="$t('cancel_confirmation')" :cancelText="$t('no')" :okText="$t('yes')" @confirm="() => cancel(record.key)">
              <a>
                <a-tooltip :title="$t('cancel')" placement="bottom">
                  <a-icon type="close-circle" theme="twoTone" two-tone-color="#eb2f96"/>
                </a-tooltip>
              </a>
            </a-popconfirm>
          </span>
          <span v-else>
            <a :class="{'disabled': editingKey !== ''}" :disabled="editingKey !== ''" @click="() => edit(record.key)">
              <a-icon type="edit" :theme="editingKey === ''? 'twoTone' : 'outlined'" />
            </a>
            <a-popconfirm :title="$t('remove_confirmation')" :cancelText="$t('no')" :okText="$t('yes')" @confirm="() => remove(record.key)">
              <a-icon slot="icon" type="question-circle-o" style="color: red" />
              <a :class="{'disabled': editingKey !== ''}" :disabled="editingKey !== ''">
                <a-icon type="delete" :theme="editingKey === ''? 'twoTone' : 'outlined'" two-tone-color="#eb2f96" />
              </a>
            </a-popconfirm>
          </span>
        </div>
      </template>
    </a-table>

    <!-- Versión móvil para "md" y menores -->
    <div class="block md:hidden">
      <div v-for="(item, index) in items" :key="index" class="">
        <b-card class="border">
        <div v-for="col in columnsWithoutActions" :key="col.key" class="mb-2">
          <strong>{{ col.title }}: </strong>
          <a-select
            v-if="col.fieldType ==='multiselect'"
            :default-value="getSavedValue(item.key, col.dataIndex, true)"
            mode="multiple"
            style="width: 100%"
            :placeholder="$t('multiselect_placeholder')"
            :notFoundContent="$t('not_found_content')"
            optionFilterProp="label"
            @change="items => handleChange(items, col.key, col, true)"
          >
            <a-select-option v-for="opt in getSelectorOptions(col.dataIndex)" :key="opt.key" :label="item.label">
              {{ opt.label }}
              <a-switch
                v-if="col.dataIndex === 'passengers'"
                size="small"
                :checked="getPrimaryPassenger(item, opt)"
                @change="setPrimaryPassenger(item, opt)"/>
            </a-select-option>
          </a-select>
          <a-select
            v-else-if="col.fieldType ==='singleselect'"
            allowClear
            :default-value="getSavedValue(item.key, col.dataIndex, false)"
            style="width: 100%"
            :placeholder="$t('multiselect_placeholder')"
            :notFoundContent="$t('not_found_content')"
            optionFilterProp="label"
            @change="items => handleChange(items, col.key, col, true)"
          >
            <a-select-option v-for="item in getSelectorOptions(col.dataIndex)" :key="item.key" :label="item.label">
              {{ item.label }}
            </a-select-option>
          </a-select>
          <span v-else>{{ item[col.dataIndex] }}</span>
        </div>
        </b-card>
      </div>
    </div>
  </div>
</template>
<script>
import moment from 'moment'
import LmTimePickerMask from '@/components/form/elements/LmTimePickerMask'

export default {
  components: { LmTimePickerMask },
  props: {
    newEditingKey: {
      type: String,
      default: ''
    },
    activitiesBeingEdited: {
      type: Number,
      default: 0
    },
    tableTitle: {
      type: String,
      default: null
    },
    tableFooter: {
      type: Boolean,
      default: false
    },
    timePickerFormat: {
      type: String,
      default: 'HH:mm'
    },
    timepickerMinuteStep: {
      type: Number,
      default: 5
    },
    timePickerSize: {
      type: String,
      default: 'small'
    },
    datePickerSize: {
      type: String,
      default: 'small'
    },
    datePickerFormat: {
      type: String,
      default: 'DD-MM-YYYY'
    },
    queryDateFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    datePickerStartDate: {
      type: Date,
      default: null
    },
    datePickerEndDate: {
      type: Date,
      default: null
    },
    columns: {
      type: Array,
      required: true
    },
    initialItems: {
      type: Array,
      required: true,
      default: () => []
    },
    tableSize: {
      type: String,
      default: 'default'
    },
    selectorOptions: {
      type: Object,
      default: () => {}
    },
    disableAddButton: {
      type: Boolean,
      default: false
    },
    defaultExpandAllRows: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      expandedRowKeys: [],
      items: [...this.initialItems],
      cacheItems: this.initialItems.map(item => ({ ...item })),
      editingKey: ''
    }
  },
  computed: {
    addButtonDisabled () {
      return this.datePickerStartDate === null || this.datePickerEndDate === null || this.activitiesBeingEdited > 0 || this.disableAddButton
    },
    columnsInExpandableRow () {
      const a = this.columns.filter(col => (col?.includeInExpandableRow)).map(col => (col.dataIndex))
      return a
    },
    columnsInMainRow () {
      const a = this.columns.filter(col => (col?.includeInMainRow))
      return a
    },
    columnsInMainRowButActions () {
      const a = this.columns.filter(col => (col.dataIndex !== 'actions' && col?.includeInMainRow)).map(col => (col.dataIndex))
      return a
    },
    columnsWithoutActions () {
      const a = this.columns.filter(col => (col.dataIndex !== 'actions'))
      return a
    }
  },
  watch: {
    newEditingKey (newKey) {
      if (newKey !== '') {
        this.editingKey = newKey
      }
    },
    initialItems: {
      deep: true,
      handler (newItems) {
        // get the items that has been added in the newItems array
        const diff = newItems.filter(newItem => !this.items.some(item => (item.key === newItem._id || item.key === newItem.key)))
        this.items = this.addKeyToItems(newItems)
        this.cacheItems = this.items.map(item => ({ ...item }))
        this.expandedRowKeys.push(...diff.map(item => item?._id || parseInt(item.key)))
      }
    }
  },
  methods: {
    getPrimaryPassenger (record, item) {
      return record.primaryPassenger === item.key
    },
    setPrimaryPassenger (record, item) {
      record.primaryPassenger = item.key
    },
    /**
     * Generate a time mask based on input value (23:59)
     * @param {string} value
     */
    timeMask (value) {
      const hours = [
        /[0-2]/,
        value.charAt(0) === '2' ? /[0-3]/ : /[0-9]/
      ]
      const minutes = [/[0-5]/, /[0-9]/]
      return value.length > 2
        ? [...hours, ':', ...minutes]
        : hours
    },
    /**
     * Generate a time range mask based on input value (00:00-23:59)
     * @param {string} value
     */
    timeRangeMask (value) {
      const numbers = value.replace(/[^0-9]/g, '')
      if (numbers.length > 4) {
        return [...this.timeMask(numbers.substring(0, 4)), '-', ...this.timeMask(numbers.substring(4))]
      }
      return [...this.timeMask(numbers)]
    },
    handleKeyDown (event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        const record = this.items.find(item => item.editable)
        if (record) {
          this.save(record)
        }
      }
    },
    toggleExpandedRowKeys () {
      if (this.expandedRowKeys.length === 0) {
        this.expandedRowKeys = this.items.map(item => item.key)
      } else {
        this.expandedRowKeys = []
      }
    },
    getSavedValue (key, column, isMultiSelect = false) {
      const target = this.items.find(item => key === item.key)
      if (column in target && target[column]) {
        return target[column]
      } else {
        return isMultiSelect ? [] : ''
      }
    },
    getRecordLabelFromSelectorOptionsIfExists (key, options) {
      if (options) {
        const selectedRecord = this.selectorOptions[key].find(item => item.key === options[key])
        if (selectedRecord) {
          return selectedRecord.key
        } else {
          return null
        }
      } else {
        return null
      }
    },
    getRecordsLabelsFromSelectorOptions (key, options) {
      if (Array.isArray(options) && options.length > 0) {
        const selectedRecords = this.selectorOptions[key].filter(item => options.includes(item.key))
        return selectedRecords
      } else if (options) {
        const selectedRecord = this.selectorOptions[key].find(item => item.key === options)
        if (selectedRecord) {
          return [selectedRecord]
        } else {
          return null
        }
      } else {
        return null
      }
    },
    datePickerDisabledDate (date) {
      if (this.datePickerStartDate && this.datePickerEndDate) {
        return date < moment(this.datePickerStartDate) || date > moment(this.datePickerEndDate).endOf('day')
      }
      return false
    },
    getSelectorOptions (colName) {
      return this.selectorOptions?.[colName] || []
    },
    columnTitle (colName) {
      const columnAttributes = this.columns.find(col => col.dataIndex === colName)
      return columnAttributes?.title || colName
    },
    dailyTargetLink (colName, value) {
      const columnAttributes = this.columns.find(col => col.dataIndex === colName)
      const currentDate = moment(value, this.datePickerFormat).format(this.queryDateFormat)
      if (columnAttributes?.dailyTargetLink) {
        return `${columnAttributes?.dailyTargetLink}?cd=${currentDate}`
      } else {
        return null
      }
    },
    columnTargetLink (colName, id = '', record) {
      const columnAttributes = this.columns.find(col => col.dataIndex === colName)
      if (columnAttributes?.targetLink) {
        if (record?.date) {
          return `${columnAttributes?.targetLink}${id}?cd=${moment(record.date, this.datePickerFormat).format(this.queryDateFormat)}`
        } else {
          return `${columnAttributes?.targetLink}${id}`
        }
      } else {
        return null
      }
    },
    columnFieldType (colName) {
      const columnAttributes = this.columns.find(col => col.dataIndex === colName)
      return columnAttributes?.fieldType || 'text'
    },
    columnFieldTagColor (colName) {
      const columnAttributes = this.columns.find(col => col.dataIndex === colName)
      return columnAttributes?.fieldTagColor || 'volcano'
    },
    columnPosition (colName) {
      return this.columns.findIndex(col => col.dataIndex === colName)
    },
    handleChange (value, key, column, isMultiSelect = false) {
      const newItems = [...this.items]
      const target = newItems.find(item => key === item.key)
      if (target) {
        target[column] = value
        this.items = newItems
        if (isMultiSelect && column === 'passengers' && Array.isArray(value) && value.length === 1) {
          target.primaryPassenger = value[0]
        }
      }
    },
    edit (key) {
      const newItems = [...this.items]
      const target = newItems.find(item => key === item.key)
      this.editingKey = key
      if (target) {
        target.editable = true
        this.items = newItems
        this.$emit('activityEdited')
        if (('_id' in target)) {
          this.$emit('setEditableOnActivity', key)
        }
      }
    },
    save (record) {
      record.editable = false
      const newItems = [...this.items]
      const newCacheItems = [...this.cacheItems]
      const target = newItems.find(item => record.key === item.key)
      const targetCache = newCacheItems.find(item => record.key === item.key)
      if (target && targetCache) {
        delete target.editable
        this.items = newItems
        Object.assign(targetCache, target)
        this.cacheItems = newCacheItems
      }
      this.editingKey = ''
      this.$emit('updateItems', this.items)
      this.$emit('activitySaved')
    },
    cancel (key) {
      const newItems = [...this.items]
      const target = newItems.find(item => key === item.key)
      this.editingKey = ''
      if (target) {
        Object.assign(target, this.cacheItems.find(item => key === item.key))
        delete target.editable
        this.items = newItems
        this.$emit('clearEditableOnActivity', target.key)
        this.$emit('activityCanceled')
      }
    },
    remove (key) {
      const newItems = [...this.items]
      const target = newItems.find(item => key === item.key)
      if (target) {
        newItems.splice(newItems.indexOf(target), 1)
        this.items = newItems
      }
      this.$emit('updateItems', this.items)
      this.$emit('activityRemoved')
    },
    addKeyToItems (items) {
      return items.map((item, index) => ({ ...item, key: item?._id ? item._id : index }))
    }
  },
  created () {
    this.items = this.addKeyToItems(this.initialItems)
    this.expandedRowKeys = this.items.map(item => item.key)
    this.cacheItems = this.initialItems.map(item => ({ ...item }))
  }
}
</script>
<style >
.editable-row-operations a {
  margin-right: 8px;
}
tr.ant-table-expanded-row {
  background: whitesmoke !important;
}

.ant-table-thead > tr > th, .ant-table-tbody > tr > td {
  padding: 5px 5px;
}

tr.ant-table-expanded-row {
  background: lightgoldenrodyellow !important;
}
</style>
